/**
 * Client profile defaults + client-link binding helpers.
 * Used by state migration, sidebar editing UI, preview rendering, and exporters.
 */

import { iterateAllSections } from './iterate-sections.js';
import { escapeHTML } from './html-escape.js';

export const CLIENT_LINK_MARKER_PREFIX = 'pb-client://';

function cleanString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizePhone(value) {
  const raw = cleanString(value);
  if (!raw) return '';
  const hasPlus = raw.startsWith('+');
  const digits = raw.replace(/\D+/g, '');
  if (!digits) return '';
  return hasPlus ? `+${digits}` : digits;
}

export function normalizeDomain(value) {
  return cleanString(value)
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/g, '');
}

/**
 * Bare hostname for Umami's data-domains attribute — stricter than
 * normalizeDomain. Umami compares the attribute against location.hostname
 * literally, so a value carrying a path, port, or typo silently disables ALL
 * tracking rather than failing loudly. Anything that does not resolve to a
 * dotted hostname returns '' so the caller can omit the attribute instead.
 */
export function normalizeAnalyticsDomain(value) {
  const raw = cleanString(value).toLowerCase();
  if (!raw) return '';
  let host;
  try {
    host = new URL(raw.includes('://') ? raw : `https://${raw}`).hostname;
  } catch {
    return '';
  }
  // Requires at least one dot and an alphabetic TLD: rejects 'localhost',
  // bare IPs, and half-typed values that would silently suppress tracking.
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/.test(host))
    return '';
  return host;
}

/**
 * Both the bare and www. forms of a hostname. A site reachable at both would
 * otherwise report from only whichever one was typed into the field.
 */
export function analyticsDomainVariants(value) {
  const host = normalizeAnalyticsDomain(value);
  if (!host) return [];
  const bare = host.replace(/^www\./, '');
  return bare === host ? [bare, `www.${bare}`] : [bare, host];
}

function createDefaultAddress() {
  return {
    lineOne: '',
    lineTwo: '',
    city: '',
    county: '',
    postcode: '',
    mapLink: ''
  };
}

function createDefaultSocial() {
  return {
    facebook: '',
    instagram: '',
    linkedin: '',
    x: '',
    youtube: '',
    yelp: ''
  };
}

export function createDefaultClientInfo(projectName = 'Untitled') {
  return {
    name: cleanString(projectName) || 'Untitled',
    tagline: '',
    email: '',
    phone: '',
    phoneFormatted: '',
    domain: '',
    address: createDefaultAddress(),
    social: createDefaultSocial()
  };
}

export function normalizeClientInfo(clientInfo, projectName = 'Untitled') {
  const defaults = createDefaultClientInfo(projectName);
  const source = clientInfo && typeof clientInfo === 'object' ? clientInfo : {};
  const addressSource = source.address && typeof source.address === 'object' ? source.address : {};
  const socialSource = source.social && typeof source.social === 'object' ? source.social : {};

  const normalizedPhone = normalizePhone(source.phone || source.phoneFormatted || '');

  return {
    name: cleanString(source.name) || defaults.name,
    tagline: cleanString(source.tagline),
    email: cleanString(source.email),
    phone: normalizedPhone,
    phoneFormatted: cleanString(source.phoneFormatted) || cleanString(source.phone),
    domain: normalizeDomain(source.domain),
    address: {
      lineOne: cleanString(addressSource.lineOne),
      lineTwo: cleanString(addressSource.lineTwo),
      city: cleanString(addressSource.city),
      county: cleanString(addressSource.county),
      postcode: cleanString(addressSource.postcode),
      mapLink: cleanString(addressSource.mapLink)
    },
    social: {
      facebook: cleanString(socialSource.facebook),
      instagram: cleanString(socialSource.instagram),
      linkedin: cleanString(socialSource.linkedin),
      x: cleanString(socialSource.x),
      youtube: cleanString(socialSource.youtube),
      yelp: cleanString(socialSource.yelp)
    }
  };
}

/**
 * Single-line postal address: "lineOne City, County Postcode", with every empty
 * part dropped. Exported because the 11ty exporter bakes the same string into
 * the form endpoint's confirmation email, and a second copy of this join would
 * drift from the address the site itself renders.
 */
export function resolveAddressBlockValue(client) {
  const lineOne = cleanString(client?.address?.lineOne);
  const city = cleanString(client?.address?.city);
  const county = cleanString(client?.address?.county);
  const postcode = cleanString(client?.address?.postcode);
  const locality = [city, county].filter(Boolean).join(', ');
  return [lineOne, locality, postcode].filter(Boolean).join(' ').trim();
}

/**
 * Scan all section HTML in state for client bindings (href markers + text markers).
 * Returns a Set of binding keys actually in use (e.g. {'phone', 'phoneFormatted', 'addressBlock'}).
 */
export function collectUsedClientKeys(state) {
  const keys = new Set();
  if (!state) return keys;
  const hrefRe = /pb-client:\/\/([a-zA-Z0-9_-]+)/g;
  const spanRe = /data-pb-client=\x22([a-zA-Z0-9_-]+)\x22/g;
  const anchorTextRe = /data-pb-client-text=\x22([a-zA-Z0-9_-]+)\x22/g;

  const scan = (html) => {
    if (!html || typeof html !== 'string') return;
    let m;
    while ((m = hrefRe.exec(html)) !== null) {
      if (getBindingByKey(m[1])) keys.add(m[1]);
    }
    hrefRe.lastIndex = 0;
    while ((m = spanRe.exec(html)) !== null) {
      if (CLIENT_TEXT_BINDING_MAP[m[1]]) keys.add(m[1]);
    }
    spanRe.lastIndex = 0;
    while ((m = anchorTextRe.exec(html)) !== null) {
      if (CLIENT_TEXT_BINDING_MAP[m[1]]) keys.add(m[1]);
    }
    anchorTextRe.lastIndex = 0;
  };

  // Page sections
  for (const page of state.pages || []) {
    for (const section of page.sections || []) scan(section.html);
  }
  // Shared sections (navigation, footer)
  if (state.sharedSections && typeof state.sharedSections === 'object') {
    for (const role of Object.values(state.sharedSections)) {
      if (role) scan(role.html);
    }
  }
  return keys;
}

/**
 * Map binding key → accessor that reads the resolved value from a normalized clientInfo.
 * Covers both link-binding keys and text-binding keys so validateClientInfoForExport
 * emits warnings for any empty-but-used field (text or href).
 */
const KEY_VALUE_ACCESSORS = {
  phone: (c) => c.phone,
  phoneFormatted: (c) => c.phoneFormatted || c.phone,
  email: (c) => c.email,
  mapLink: (c) => c.address.mapLink,
  domain: (c) => c.domain,
  name: (c) => c.name,
  tagline: (c) => c.tagline,
  addressLineOne: (c) => c.address.lineOne,
  addressLineTwo: (c) => c.address.lineTwo,
  addressCity: (c) => c.address.city,
  addressCounty: (c) => c.address.county,
  addressPostcode: (c) => c.address.postcode,
  addressBlock: (c) => resolveAddressBlockValue(c),
  facebook: (c) => c.social.facebook,
  instagram: (c) => c.social.instagram,
  linkedin: (c) => c.social.linkedin,
  x: (c) => c.social.x,
  youtube: (c) => c.social.youtube,
  yelp: (c) => c.social.yelp
};

const TEXT_BINDING_PROTECTED_SELECTOR = [
  'script',
  'style',
  'svg',
  'img',
  'picture',
  'source',
  'canvas',
  'video',
  'audio',
  '[aria-hidden="true"]',
  '.cs-icon',
  '.cs-icon-wrapper',
  '[class*="icon"]'
].join(',');

function collectAllSectionHTML(state) {
  const htmlList = [];
  for (const page of state?.pages || []) {
    for (const section of page?.sections || []) {
      if (typeof section?.html === 'string' && section.html) htmlList.push(section.html);
    }
  }
  if (state?.sharedSections && typeof state.sharedSections === 'object') {
    for (const shared of Object.values(state.sharedSections)) {
      if (typeof shared?.html === 'string' && shared.html) htmlList.push(shared.html);
    }
  }
  return htmlList;
}

function extractTextFromSectionHTML(html) {
  if (!html || typeof html !== 'string') return '';
  if (typeof DOMParser === 'undefined') return '';
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    return root ? String(root.textContent || '') : '';
  } catch {
    return '';
  }
}

function escapeWordForRegex(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validate clientInfo completeness against actually-used bindings.
 * Returns { warnings: string[] } — one warning per empty-but-used field.
 */
export function validateClientInfoForExport(state) {
  const warnings = [];
  if (!state) return { warnings };

  const usedKeys = collectUsedClientKeys(state);
  if (usedKeys.size === 0) return { warnings };

  const seenEmptyKeys = new Set();
  for (const entry of findBoundButEmptyFields(state)) {
    if (seenEmptyKeys.has(entry.key)) continue;
    seenEmptyKeys.add(entry.key);
    warnings.push(`${entry.label} is used in sections but has no value set`);
  }

  const forbiddenWords = Array.isArray(state?.brandVoice?.forbiddenWords)
    ? state.brandVoice.forbiddenWords
        .map((word) => (typeof word === 'string' ? word.trim().toLowerCase() : ''))
        .filter(Boolean)
    : [];
  if (forbiddenWords.length > 0 && typeof DOMParser !== 'undefined') {
    const sectionText = collectAllSectionHTML(state)
      .map((html) => extractTextFromSectionHTML(html))
      .join(' ')
      .toLowerCase();
    for (const word of forbiddenWords) {
      const pattern = new RegExp(`\\b${escapeWordForRegex(word)}\\b`, 'i');
      if (pattern.test(sectionText)) {
        warnings.push(`Brand voice forbidden word detected in content: "${word}"`);
      }
    }
  }
  return { warnings };
}

const CLIENT_FIELD_PATHS = {
  phone: 'clientInfo.phone',
  phoneFormatted: 'clientInfo.phoneFormatted',
  email: 'clientInfo.email',
  mapLink: 'clientInfo.address.mapLink',
  domain: 'clientInfo.domain',
  name: 'clientInfo.name',
  tagline: 'clientInfo.tagline',
  addressLineOne: 'clientInfo.address.lineOne',
  addressLineTwo: 'clientInfo.address.lineTwo',
  addressCity: 'clientInfo.address.city',
  addressCounty: 'clientInfo.address.county',
  addressPostcode: 'clientInfo.address.postcode',
  addressBlock: 'clientInfo.address.lineOne',
  facebook: 'clientInfo.social.facebook',
  instagram: 'clientInfo.social.instagram',
  linkedin: 'clientInfo.social.linkedin',
  x: 'clientInfo.social.x',
  youtube: 'clientInfo.social.youtube',
  yelp: 'clientInfo.social.yelp'
};

function labelForClientKey(key) {
  const linkBinding = getBindingByKey(key);
  const textBinding = CLIENT_TEXT_BINDING_MAP[key];
  return linkBinding ? linkBinding.label : textBinding ? textBinding.label : key;
}

function nearestEidBefore(html, index) {
  const prefix = html.slice(0, Math.max(0, index));
  const matches = [...prefix.matchAll(/data-pb-eid=([\x22\x27])(.*?)\1/g)];
  return matches.length ? matches[matches.length - 1][2] : '';
}

function pushEmptyBinding(out, state, client, ctx, key, index, kind) {
  const accessor = KEY_VALUE_ACCESSORS[key];
  if (!accessor) return;
  if (accessor(client)) return;
  out.push({
    key,
    label: labelForClientKey(key),
    path: CLIENT_FIELD_PATHS[key] || `clientInfo.${key}`,
    sectionPath: ctx.sectionPath,
    instanceId: ctx.instanceId,
    pageId: ctx.pageId || null,
    pageName: ctx.pageName || '',
    sharedKind: ctx.sharedKind || null,
    eid: nearestEidBefore(ctx.html, index),
    kind
  });
}

export function findBoundButEmptyFields(state) {
  const out = [];
  if (!state) return out;
  const client = normalizeClientInfo(state.clientInfo, state.projectName);
  iterateAllSections(state, (ctx) => {
    const html = ctx.html || '';
    let match;
    const hrefRe = /pb-client:\/\/([a-zA-Z0-9_-]+)/g;
    while ((match = hrefRe.exec(html)) !== null) {
      if (getBindingByKey(match[1]))
        pushEmptyBinding(out, state, client, ctx, match[1], match.index, 'href');
    }
    const textRe = /data-pb-client(?:-text)?=([\x22\x27])([a-zA-Z0-9_-]+)\1/g;
    while ((match = textRe.exec(html)) !== null) {
      if (CLIENT_TEXT_BINDING_MAP[match[2]])
        pushEmptyBinding(out, state, client, ctx, match[2], match.index, 'text');
    }
  });
  return out;
}

const CLIENT_LINK_BINDING_DEFS = [
  {
    key: 'phone',
    label: 'Client Phone',
    template: 'tel:{{ client.phone }}',
    conditionalTemplate: '{% if client.phone %}tel:{{ client.phone }}{% else %}#{% endif %}',
    resolve: (client) => (client.phone ? `tel:${client.phone}` : '')
  },
  {
    key: 'email',
    label: 'Client Email',
    template: 'mailto:{{ client.email }}',
    conditionalTemplate: '{% if client.email %}mailto:{{ client.email }}{% else %}#{% endif %}',
    resolve: (client) => (client.email ? `mailto:${client.email}` : '')
  },
  {
    key: 'mapLink',
    label: 'Client Map Link',
    template: '{{ client.address.mapLink }}',
    conditionalTemplate:
      '{% if client.address.mapLink %}{{ client.address.mapLink }}{% else %}#{% endif %}',
    resolve: (client) => client.address.mapLink || ''
  },
  {
    key: 'domain',
    label: 'Client Domain',
    template: 'https://{{ client.domain }}',
    conditionalTemplate: '{% if client.domain %}https://{{ client.domain }}{% else %}#{% endif %}',
    resolve: (client) => (client.domain ? `https://${client.domain}` : '')
  },
  {
    key: 'facebook',
    label: 'Social: Facebook',
    template: '{{ client.social.facebook }}',
    conditionalTemplate:
      '{% if client.social.facebook %}{{ client.social.facebook }}{% else %}#{% endif %}',
    resolve: (client) => client.social.facebook || ''
  },
  {
    key: 'instagram',
    label: 'Social: Instagram',
    template: '{{ client.social.instagram }}',
    conditionalTemplate:
      '{% if client.social.instagram %}{{ client.social.instagram }}{% else %}#{% endif %}',
    resolve: (client) => client.social.instagram || ''
  },
  {
    key: 'linkedin',
    label: 'Social: LinkedIn',
    template: '{{ client.social.linkedin }}',
    conditionalTemplate:
      '{% if client.social.linkedin %}{{ client.social.linkedin }}{% else %}#{% endif %}',
    resolve: (client) => client.social.linkedin || ''
  },
  {
    key: 'x',
    label: 'Social: X',
    template: '{{ client.social.x }}',
    conditionalTemplate: '{% if client.social.x %}{{ client.social.x }}{% else %}#{% endif %}',
    resolve: (client) => client.social.x || ''
  },
  {
    key: 'youtube',
    label: 'Social: YouTube',
    template: '{{ client.social.youtube }}',
    conditionalTemplate:
      '{% if client.social.youtube %}{{ client.social.youtube }}{% else %}#{% endif %}',
    resolve: (client) => client.social.youtube || ''
  },
  {
    key: 'yelp',
    label: 'Social: Yelp',
    template: '{{ client.social.yelp }}',
    conditionalTemplate:
      '{% if client.social.yelp %}{{ client.social.yelp }}{% else %}#{% endif %}',
    resolve: (client) => client.social.yelp || ''
  }
];

export const CLIENT_LINK_BINDINGS = CLIENT_LINK_BINDING_DEFS.map(({ key, label }) => ({
  key,
  label
}));

function getBindingByKey(key) {
  return CLIENT_LINK_BINDING_DEFS.find((entry) => entry.key === key) || null;
}

// --- Sitewide text bindings (data-pb-client / data-pb-client-text) ---
// These markers appear inside section HTML and cascade clientInfo values into
// display text — in preview they resolve to concrete strings, in 11ty export
// they resolve to Nunjucks expressions read from src/_data/client.json.

const CLIENT_TEXT_BINDING_DEFS = [
  {
    key: 'name',
    label: 'Business Name',
    resolve: (c) => c.name || '',
    template: '{{ client.name }}'
  },
  {
    key: 'tagline',
    label: 'Tagline',
    resolve: (c) => c.tagline || '',
    template: '{{ client.tagline }}'
  },
  { key: 'email', label: 'Email', resolve: (c) => c.email || '', template: '{{ client.email }}' },
  {
    key: 'phone',
    label: 'Phone (digits)',
    resolve: (c) => c.phone || '',
    template: '{{ client.phone }}'
  },
  {
    key: 'phoneFormatted',
    label: 'Phone',
    resolve: (c) => c.phoneFormatted || c.phone || '',
    template: '{{ client.phoneFormatted }}'
  },
  {
    key: 'domain',
    label: 'Domain',
    resolve: (c) => c.domain || '',
    template: '{{ client.domain }}'
  },
  {
    key: 'addressLineOne',
    label: 'Address Line 1',
    resolve: (c) => c.address.lineOne || '',
    template: '{{ client.address.lineOne }}'
  },
  {
    key: 'addressLineTwo',
    label: 'Address Line 2',
    resolve: (c) => c.address.lineTwo || '',
    template: '{{ client.address.lineTwo }}'
  },
  {
    key: 'addressCity',
    label: 'City',
    resolve: (c) => c.address.city || '',
    template: '{{ client.address.city }}'
  },
  {
    key: 'addressCounty',
    label: 'State / County',
    resolve: (c) => c.address.county || '',
    template: '{{ client.address.county }}'
  },
  {
    key: 'addressPostcode',
    label: 'ZIP / Postcode',
    resolve: (c) => c.address.postcode || '',
    template: '{{ client.address.postcode }}'
  },
  {
    key: 'addressBlock',
    label: 'Full Address',
    resolve: (c) => resolveAddressBlockValue(c),
    template:
      '{% if client.address.lineOne %}{{ client.address.lineOne }}{% endif %}' +
      '{% if client.address.city %}{% if client.address.lineOne %}, {% endif %}{{ client.address.city }}{% endif %}' +
      '{% if client.address.county %}{% if client.address.lineOne or client.address.city %}, {% endif %}{{ client.address.county }}{% endif %}' +
      '{% if client.address.postcode %}{% if client.address.lineOne or client.address.city or client.address.county %} {% endif %}{{ client.address.postcode }}{% endif %}'
  },
  {
    key: 'mapLink',
    label: 'Map Link',
    resolve: (c) => c.address.mapLink || '',
    template: '{{ client.address.mapLink }}'
  },
  {
    key: 'facebook',
    label: 'Social: Facebook',
    resolve: (c) => c.social.facebook || '',
    template: '{{ client.social.facebook }}'
  },
  {
    key: 'instagram',
    label: 'Social: Instagram',
    resolve: (c) => c.social.instagram || '',
    template: '{{ client.social.instagram }}'
  },
  {
    key: 'linkedin',
    label: 'Social: LinkedIn',
    resolve: (c) => c.social.linkedin || '',
    template: '{{ client.social.linkedin }}'
  },
  {
    key: 'x',
    label: 'Social: X',
    resolve: (c) => c.social.x || '',
    template: '{{ client.social.x }}'
  },
  {
    key: 'youtube',
    label: 'Social: YouTube',
    resolve: (c) => c.social.youtube || '',
    template: '{{ client.social.youtube }}'
  },
  {
    key: 'yelp',
    label: 'Social: Yelp',
    resolve: (c) => c.social.yelp || '',
    template: '{{ client.social.yelp }}'
  }
];

const CLIENT_TEXT_BINDING_MAP = Object.fromEntries(CLIENT_TEXT_BINDING_DEFS.map((d) => [d.key, d]));

export const CLIENT_TEXT_BINDINGS = CLIENT_TEXT_BINDING_DEFS.map(({ key, label }) => ({
  key,
  label
}));

export function getClientTextBindings() {
  return CLIENT_TEXT_BINDINGS;
}

export function getClientTextBindingByKey(key) {
  return CLIENT_TEXT_BINDING_MAP[key] || null;
}

/**
 * Resolve a client text binding to its runtime value.
 * mode='preview' or 'flat' returns the concrete string (or '' if unset).
 * mode='11ty' returns the Nunjucks template expression.
 */
export function resolveClientText(key, clientInfo, mode = 'preview', projectName = 'Untitled') {
  const def = CLIENT_TEXT_BINDING_MAP[key];
  if (!def) return '';
  if (mode === '11ty') return def.template;
  if (mode === 'astro') return astroSentinel('TXT', key);
  const client = normalizeClientInfo(clientInfo, projectName);
  return def.resolve(client);
}

export function makeClientLinkMarker(key) {
  const binding = getBindingByKey(key);
  return binding ? `${CLIENT_LINK_MARKER_PREFIX}${binding.key}` : '';
}

export function parseClientLinkMarker(value) {
  const href = cleanString(value);
  if (!href.startsWith(CLIENT_LINK_MARKER_PREFIX)) return '';
  const key = href.slice(CLIENT_LINK_MARKER_PREFIX.length).trim();
  return getBindingByKey(key) ? key : '';
}

export function isClientLinkMarker(value) {
  return !!parseClientLinkMarker(value);
}

export function resolveClientLinkHref(
  href,
  clientInfo,
  mode = 'preview',
  projectName = 'Untitled'
) {
  const key = parseClientLinkMarker(href);
  if (!key) return href || '';

  const binding = getBindingByKey(key);
  if (!binding) return href || '';
  if (mode === '11ty') return binding.conditionalTemplate;
  if (mode === 'astro') return astroSentinel('HREF', key);

  const client = normalizeClientInfo(clientInfo, projectName);
  const resolved = binding.resolve(client);
  return resolved || '#';
}

/**
 * Resolve href + text bindings in raw section HTML.
 *
 * Markers:
 *   - <a href="pb-client://KEY">                     — href binding (legacy)
 *   - <a ... data-pb-client-text="TEXTKEY">         — explicit anchor-text binding
 *   - <span data-pb-client="TEXTKEY">               — standalone/prose text binding
 *
 * Modes:
 *   - 'preview' / 'flat' — resolve to concrete strings (href values, text content)
 *   - '11ty'             — resolve hrefs to Nunjucks conditionals, text to {{ client.X }}
 *   - 'astro'            — resolve to sentinels restored as ${client.X} in .astro
 *
 * After resolving, marker attributes are removed so exported HTML contains no
 * data-pb-* artifacts (and preview DOM stays clean on each re-render).
 */
export function rewriteClientLinksInHTML(
  html,
  clientInfo,
  mode = 'preview',
  projectName = 'Untitled'
) {
  if (!html || typeof html !== 'string') return '';
  const hasHrefMarker = html.includes(CLIENT_LINK_MARKER_PREFIX);
  const hasTextMarker = html.includes('data-pb-client');
  if (!hasHrefMarker && !hasTextMarker) return html;

  if (typeof DOMParser === 'undefined') {
    return rewriteWithRegexFallback(html, clientInfo, mode, projectName);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;

  // Pass 1 — href bindings (pb-client:// on <a>)
  for (const anchor of root.querySelectorAll('a[href]')) {
    const currentHref = anchor.getAttribute('href') || '';
    if (!isClientLinkMarker(currentHref)) continue;

    const linkKey = parseClientLinkMarker(currentHref);
    anchor.setAttribute('href', resolveClientLinkHref(currentHref, clientInfo, mode, projectName));

    // Pass 4 (back-compat) — placeholder-text heuristic for anchors WITHOUT an
    // explicit data-pb-client-text. Only fires for the old link keys because
    // older projects relied on this implicit behavior.
    if (
      (mode === '11ty' || mode === 'astro') &&
      linkKey &&
      anchor.children.length === 0 &&
      !anchor.hasAttribute('data-pb-client-text')
    ) {
      const text = (anchor.textContent || '').trim();
      if (text && isPlaceholderTextForBinding(text, linkKey)) {
        const tpl = TEXT_TEMPLATES[linkKey];
        if (tpl)
          anchor.textContent =
            mode === 'astro' ? astroSentinel('LGCY', linkKey) : `__PB_LGCY_${linkKey}__`;
      }
    }
  }

  // Pass 2 — explicit anchor-text bindings (data-pb-client-text on <a>)
  for (const anchor of root.querySelectorAll('a[data-pb-client-text]')) {
    const textKey = anchor.getAttribute('data-pb-client-text') || '';
    const def = CLIENT_TEXT_BINDING_MAP[textKey];
    if (def) {
      applyTextBinding(anchor, textKey, def, mode, clientInfo, projectName);
    }
    anchor.removeAttribute('data-pb-client-text');
  }

  // Pass 3 — standalone/prose text bindings (data-pb-client on any element)
  for (const el of root.querySelectorAll('[data-pb-client]')) {
    const textKey = el.getAttribute('data-pb-client') || '';
    const def = CLIENT_TEXT_BINDING_MAP[textKey];
    if (def) {
      applyTextBinding(el, textKey, def, mode, clientInfo, projectName);
    }
    el.removeAttribute('data-pb-client');
  }

  let result = root.innerHTML;

  // Post-serialization: swap sentinels to Nunjucks (DOMParser would escape {{ }})
  if (mode === '11ty') {
    if (result.includes('__PB_TXT_')) {
      result = result.replace(/__PB_TXT_([a-zA-Z0-9]+)__/g, (_, k) => {
        const def = CLIENT_TEXT_BINDING_MAP[k];
        return def ? def.template : k;
      });
    }
    if (result.includes('__PB_LGCY_')) {
      result = result.replace(/__PB_LGCY_([a-zA-Z0-9]+)__/g, (_, k) => TEXT_TEMPLATES[k] || k);
    }
  }

  return result;
}

function applyTextBinding(element, key, def, mode, clientInfo, projectName) {
  const value =
    mode === '11ty'
      ? `__PB_TXT_${key}__`
      : mode === 'astro'
        ? astroSentinel('TXT', key)
        : def.resolve(normalizeClientInfo(clientInfo, projectName)) || '';
  setTextContentPreservingProtectedChildren(element, value);
}

function isInsideProtectedBindingContent(node, root) {
  let cur = node?.parentElement || null;
  while (cur && cur !== root) {
    if (cur.matches?.(TEXT_BINDING_PROTECTED_SELECTOR)) return true;
    cur = cur.parentElement;
  }
  return false;
}

function mutableTextNodes(element) {
  const doc = element?.ownerDocument || document;
  const walker = doc.createTreeWalker(element, 4 /* SHOW_TEXT */);
  const nodes = [];
  let node;
  while ((node = walker.nextNode())) {
    if (!node.parentNode) continue;
    if (isInsideProtectedBindingContent(node, element)) continue;
    nodes.push(node);
  }
  return nodes;
}

function setTextContentPreservingProtectedChildren(element, value) {
  if (!element) return;
  const text = String(value == null ? '' : value);
  if (!element.children || element.children.length === 0) {
    element.textContent = text;
    return;
  }

  const nodes = mutableTextNodes(element);
  let replaced = false;
  for (const node of nodes) {
    const hasMeaningfulText = /\S/.test(node.nodeValue || '');
    if (!replaced && hasMeaningfulText) {
      node.nodeValue = text;
      replaced = true;
      continue;
    }
    if (replaced && hasMeaningfulText) {
      node.nodeValue = '';
    }
  }

  if (!replaced) {
    element.appendChild((element.ownerDocument || document).createTextNode(text));
  }
}

function rewriteWithRegexFallback(html, clientInfo, mode, projectName) {
  // Href markers
  let result = html.replace(
    /href=([\x22\x27])(pb-client:\/\/[a-zA-Z0-9_-]+)\1/g,
    (_match, quote, marker) =>
      `href=${quote}${escapeHTML(resolveClientLinkHref(marker, clientInfo, mode, projectName))}${quote}`
  );
  // <span data-pb-client="KEY">…</span>
  result = result.replace(
    /<span\s+data-pb-client=\x22([a-zA-Z0-9_-]+)\x22\s*>([\s\S]*?)<\/span>/g,
    (_match, key, inner) => {
      const def = CLIENT_TEXT_BINDING_MAP[key];
      if (!def) return `<span>${inner}</span>`;
      if (mode === '11ty') return `<span>${def.template}</span>`;
      if (mode === 'astro') return `<span>${astroSentinel('TXT', key)}</span>`;
      const client = normalizeClientInfo(clientInfo, projectName);
      return `<span>${escapeHTML(def.resolve(client) || '')}</span>`;
    }
  );
  // <a ... data-pb-client-text="KEY">…</a>
  result = result.replace(
    /<a([^>]*?)\s+data-pb-client-text=\x22([a-zA-Z0-9_-]+)\x22([^>]*)>([\s\S]*?)<\/a>/g,
    (_match, pre, key, post, inner) => {
      const def = CLIENT_TEXT_BINDING_MAP[key];
      const attrs = `${pre}${post}`;
      if (!def) return `<a${attrs}>${inner}</a>`;
      if (mode === '11ty') return `<a${attrs}>${def.template}</a>`;
      if (mode === 'astro') return `<a${attrs}>${astroSentinel('TXT', key)}</a>`;
      const client = normalizeClientInfo(clientInfo, projectName);
      return `<a${attrs}>${escapeHTML(def.resolve(client) || '')}</a>`;
    }
  );
  return result;
}

// --- Placeholder detection & display text helpers ---

const PLACEHOLDER_PATTERNS = {
  phone: /^[\d\s()+-]{7,}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  domain: /^(www\.)?[a-z0-9.-]+\.[a-z]{2,}$/i,
  mapLink: /^https?:\/\/(maps\.google|goo\.gl|www\.google\.\w+\/maps)/i,
  facebook: /facebook\.com|fb\.com/i,
  instagram: /instagram\.com/i,
  linkedin: /linkedin\.com/i,
  x: /(twitter\.com|x\.com)/i,
  youtube: /youtube\.com|youtu\.be/i,
  yelp: /yelp\.com/i
};

/** Returns true if `text` looks like a generic placeholder for the given binding key. */
export function isPlaceholderTextForBinding(text, bindingKey) {
  const pattern = PLACEHOLDER_PATTERNS[bindingKey];
  if (!pattern) return false;
  return pattern.test((text || '').trim());
}

/** Returns the best display text for a binding key from current clientInfo. */
export function getDisplayTextForBinding(bindingKey, clientInfo, projectName) {
  const client = normalizeClientInfo(clientInfo, projectName);
  switch (bindingKey) {
    case 'phone':
      return client.phoneFormatted || client.phone;
    case 'email':
      return client.email;
    case 'domain':
      return client.domain;
    case 'mapLink':
      return client.address.mapLink;
    case 'facebook':
      return client.social.facebook;
    case 'instagram':
      return client.social.instagram;
    case 'linkedin':
      return client.social.linkedin;
    case 'x':
      return client.social.x;
    case 'youtube':
      return client.social.youtube;
    case 'yelp':
      return client.social.yelp;
    default:
      return '';
  }
}

/** Nunjucks text expressions for display text (not hrefs) — used in 11ty export. */
const TEXT_TEMPLATES = {
  phone: '{{ client.phoneFormatted }}',
  email: '{{ client.email }}',
  domain: '{{ client.domain }}',
  mapLink: '{{ client.address.mapLink }}',
  facebook: '{{ client.social.facebook }}',
  instagram: '{{ client.social.instagram }}',
  linkedin: '{{ client.social.linkedin }}',
  x: '{{ client.social.x }}',
  youtube: '{{ client.social.youtube }}',
  yelp: '{{ client.social.yelp }}'
};

const ASTRO_SENTINEL_RE = /__PB_ASTRO_(?:TXT|HREF|LGCY)_[a-zA-Z0-9]+__/;

export const ASTRO_CLIENT_IMPORT = "import client from '@data/client.json';";
export const ASTRO_CLIENT_ESCAPE_HELPER =
  "const pbClientEsc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;')" +
  ".replace(/>/g, '&gt;').replace(/\"/g, '&quot;').replace(/'/g, '&#39;');";

function astroSentinel(kind, key) {
  return `__PB_ASTRO_${kind}_${key}__`;
}

const ASTRO_TEXT_EXPRESSIONS = {
  name: "client.name || ''",
  tagline: "client.tagline || ''",
  email: "client.email || ''",
  phone: "client.phone || ''",
  phoneFormatted: "client.phoneFormatted || client.phone || ''",
  domain: "client.domain || ''",
  addressLineOne: "client.address?.lineOne || ''",
  addressLineTwo: "client.address?.lineTwo || ''",
  addressCity: "client.address?.city || ''",
  addressCounty: "client.address?.county || ''",
  addressPostcode: "client.address?.postcode || ''",
  addressBlock:
    "[client.address?.lineOne, [client.address?.city, client.address?.county].filter(Boolean).join(', '), client.address?.postcode].filter(Boolean).join(' ').trim()",
  mapLink: "client.address?.mapLink || ''",
  facebook: "client.social?.facebook || ''",
  instagram: "client.social?.instagram || ''",
  linkedin: "client.social?.linkedin || ''",
  x: "client.social?.x || ''",
  youtube: "client.social?.youtube || ''",
  yelp: "client.social?.yelp || ''"
};

const ASTRO_LEGACY_TEXT_EXPRESSIONS = {
  phone: ASTRO_TEXT_EXPRESSIONS.phoneFormatted,
  email: ASTRO_TEXT_EXPRESSIONS.email,
  domain: ASTRO_TEXT_EXPRESSIONS.domain,
  mapLink: ASTRO_TEXT_EXPRESSIONS.mapLink,
  facebook: ASTRO_TEXT_EXPRESSIONS.facebook,
  instagram: ASTRO_TEXT_EXPRESSIONS.instagram,
  linkedin: ASTRO_TEXT_EXPRESSIONS.linkedin,
  x: ASTRO_TEXT_EXPRESSIONS.x,
  youtube: ASTRO_TEXT_EXPRESSIONS.youtube,
  yelp: ASTRO_TEXT_EXPRESSIONS.yelp
};

const ASTRO_HREF_EXPRESSIONS = {
  phone: "client.phone ? 'tel:' + client.phone : '#'",
  email: "client.email ? 'mailto:' + client.email : '#'",
  mapLink: "client.address?.mapLink || '#'",
  domain: "client.domain ? 'https://' + client.domain : '#'",
  facebook: "client.social?.facebook || '#'",
  instagram: "client.social?.instagram || '#'",
  linkedin: "client.social?.linkedin || '#'",
  x: "client.social?.x || '#'",
  youtube: "client.social?.youtube || '#'",
  yelp: "client.social?.yelp || '#'"
};

export function hasAstroClientExpressions(html) {
  return ASTRO_SENTINEL_RE.test(String(html || ''));
}

/**
 * Sentinels → live Astro template literal interpolations, AFTER the html has
 * gone through escapeAstroTemplateLiteral(). The generated component must
 * import client and define pbClientEsc when hasAstroClientExpressions() is true.
 */
export function restoreAstroClientExpressions(escaped) {
  return String(escaped || '').replace(
    /__PB_ASTRO_(TXT|HREF|LGCY)_([a-zA-Z0-9]+)__/g,
    (_match, kind, key) => {
      const expressions =
        kind === 'HREF'
          ? ASTRO_HREF_EXPRESSIONS
          : kind === 'LGCY'
            ? ASTRO_LEGACY_TEXT_EXPRESSIONS
            : ASTRO_TEXT_EXPRESSIONS;
      const expr = expressions[key];
      return expr ? `\${pbClientEsc(${expr})}` : '';
    }
  );
}
